import axios from 'axios'
import localforage from 'localforage'
import createDataDogLog from '../utils/datadog'
import { getNetworkAvailableValue } from '../utils/networkConnected'

export const ITEMS_CACHE_CATEGORY = 'items'
export const MENUS_CACHE_CATEGORY = 'menus'
export const ATTRACT_LOOP_CACHE_CATEGORY = 'attractLoop'

//Size is in MB
export const ITEMS_CACHE_CATEGORY_MAX_SIZE = 1.5
export const MENUS_CACHE_CATEGORY_MAX_SIZE = 1.5
export const ATTRACT_LOOP_CACHE_CATEGORY_MAX_SIZE = 2
export const MAX_CACHE_ITEMS = 250

export const cacheImage = async (category = '', url) => {
  try {
    const cacheBustingUrl = `${url}?t=${new Date().getTime()}`
    const response = await axios.get(cacheBustingUrl, {
      responseType: 'blob',
      timeout: 5_000,
    })

    const blob = response?.data || null

    if (!blob || !(blob instanceof Blob)) {
      return
    }

    const imageSizeMB = (blob.size / (1024 * 1024)).toFixed(2)

    if (category === ATTRACT_LOOP_CACHE_CATEGORY && imageSizeMB > ATTRACT_LOOP_CACHE_CATEGORY_MAX_SIZE) {
      createDataDogLog(
        'error',
        `Image ${url} skipped for caching due to size limit, category: ${category}, size: ${imageSizeMB}MB`,
        {},
      )
      return
    }

    if (category === ITEMS_CACHE_CATEGORY && imageSizeMB > ITEMS_CACHE_CATEGORY_MAX_SIZE) {
      createDataDogLog(
        'error',
        `Image ${url} skipped for caching due to size limit, category: ${category}, size: ${imageSizeMB}MB`,
        {},
      )
      return
    }

    if (category === MENUS_CACHE_CATEGORY && imageSizeMB > MENUS_CACHE_CATEGORY_MAX_SIZE) {
      createDataDogLog(
        'error',
        `Image ${url} skipped for caching due to size limit, category: ${category}, size: ${imageSizeMB}MB`,
        {},
      )
      return
    }

    const dbKeysLength = await localforage.length()

    if (dbKeysLength < MAX_CACHE_ITEMS) {
      await localforage.setItem(`${category}:${url}`, blob)
    } else {
      createDataDogLog('error', `Image ${url} skipped for caching due to cache max limit`)
    }
  } catch (err) {
    return
  }
}

export const getCachedImage = async (curKey) => {
  let blob = null
  try {
    blob = await localforage.getItem(curKey)
  } catch (err) {
    createDataDogLog('error', `Error retrieving cached image ${curKey}: ${err.message}`)
    return null
  }

  if (!blob || !(blob instanceof Blob)) return null

  // turn Blob into a usable URL
  return URL.createObjectURL(blob)
}

export const getCachedImagesKeys = async (category = '') => {
  const allKeys = await localforage.keys()

  if (!category) {
    return allKeys
  }
  return allKeys.filter((key) => key.startsWith(`${category}:`))
}

export const clearImageCache = async (category, force = false) => {
  if (!getNetworkAvailableValue() && !force) return

  if (!category) {
    await localforage.clear()
    return
  }

  const keys = await getCachedImagesKeys(category)
  try {
    await Promise.all(
      keys.map(async (key) => {
        await localforage.removeItem(key)
      }),
    )
  } catch (err) {
    createDataDogLog('error', `Error clearing image cache for category ${category}: ${err.message}`)
  }
}

export const getCacheSize = async () => {
  let totalBytes = 0

  await localforage.iterate((value, key) => {
    if (value instanceof Blob) {
      totalBytes += value.size
    } else if (typeof value === 'string') {
      totalBytes += new Blob([value]).size
    } else if (value !== null && value !== undefined) {
      totalBytes += new Blob([JSON.stringify(value)]).size
    }
  })

  const totalMB = totalBytes / (1024 * 1024)
  return totalMB.toFixed(2) // return size in MB
}

export const cacheMenuItemImages = async (menuImages, itemsImages) => {
  const menuImagePromises = menuImages.map((imageUrl) => cacheImage(MENUS_CACHE_CATEGORY, imageUrl))
  const itemImagePromises = itemsImages.map((imageUrl) => cacheImage(ITEMS_CACHE_CATEGORY, imageUrl))

  await Promise.all([...menuImagePromises, ...itemImagePromises])
}
